import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMCPTasks } from '../hooks/useMCPTasks';
import { useMCPBooks } from '../hooks/useMCPBooks';
import { useMCPGym } from '../hooks/useMCPGym';
import { useMCPMeals } from '../hooks/useMCPMeals';
import { useMCPSleep } from '../hooks/useMCPSleep';
import { useFirebaseService } from '../hooks/useFirebaseService';
import { newsBookmarksService, notesService, stocksService, starredStocksService } from '../services/firebaseService';
import { API_ENDPOINTS } from '../config/api';
import { useAuth } from '../context/AuthContext';
import Footer from './Footer';

const Dashboard = () => {
  const navigate = useNavigate();
  const [isSyncing, setIsSyncing] = useState(false);
  const { user } = useAuth();
  
  const { tasks = [], loading: tasksLoading, forceRefresh: refreshTasks } = useMCPTasks();
  // Use direct Firebase endpoint instead of MCP for stocks (same as StocksApp)
  const [stocks, setStocks] = useState([]);
  const [stocksLoading, setStocksLoading] = useState(false);
  const { books = [], loading: booksLoading, loadBooks: refreshBooks } = useMCPBooks();
  const { gymSessions = [], loading: gymLoading, loadGymSessions: refreshGym } = useMCPGym();
  const { meals = [], loading: mealsLoading, loadMeals: refreshMeals } = useMCPMeals();
  const { sleepRecords = [], loading: sleepLoading, loadSleepRecords: refreshSleep } = useMCPSleep();
  
  // Firebase services for additional trackers
  const { data: newsBookmarks = [], loading: newsLoading, refresh: refreshNews } = useFirebaseService(newsBookmarksService);
  const { data: notes = [], loading: notesLoading, refresh: refreshNotes } = useFirebaseService(notesService);
  const { data: firebaseStocks = [], loading: firebaseStocksLoading, refresh: refreshFirebaseStocks } = useFirebaseService(stocksService);
  const { data: starredStocks = [], loading: starredStocksLoading, refresh: refreshStarredStocks } = useFirebaseService(starredStocksService);

  // Use Firebase service stocks count (same as StocksApp)
  const totalStocks = firebaseStocks.length;

  // Debug logging
  console.log('Dashboard data:', {
    tasks: tasks.length,
    stocks: totalStocks,
    books: books.length,
    gymSessions: gymSessions.length,
    meals: meals.length,
    sleepRecords: sleepRecords.length,
    newsBookmarks: newsBookmarks.length,
    notes: notes.length
  });
  
  // Debug books structure
  if (books.length > 0) {
    console.log('Sample book structure:', books[0]);
    console.log('Books with completed status:', books.filter(book => book.status === 'completed'));
  }
  
  // Load stocks from Firebase endpoint (same as StocksApp)
  const loadStocks = async () => {
    if (!user?.uid) return;
    
    try {
      setStocksLoading(true);
      console.log('Loading stocks for user ID:', user.uid);
      console.log('API endpoint:', API_ENDPOINTS.STOCKS_GET(user.uid));
      
      const response = await fetch(API_ENDPOINTS.STOCKS_GET(user.uid));
      const data = await response.json();
      
      console.log('Stocks API response:', data);
      
      if (data.status === 'success') {
        setStocks(data.data);
        console.log('Stocks loaded successfully:', data.data);
      } else {
        console.error('Stocks API error:', data.message);
      }
    } catch (error) {
      console.error('Error loading stocks:', error);
    } finally {
      setStocksLoading(false);
    }
  };

  // Load stocks when user changes
  useEffect(() => {
    if (user) {
      loadStocks();
    } else {
      setStocks([]);
    }
  }, [user]);

  // Debug stocks data
  console.log('Stocks data from Firebase endpoint:', stocks);
  console.log('Stocks count from Firebase endpoint:', stocks.length);
  console.log('Stocks data from Firebase service:', firebaseStocks);
  console.log('Stocks count from Firebase service:', firebaseStocks.length);
  console.log('Starred stocks data from Firebase:', starredStocks);
  console.log('Starred stocks count from Firebase:', starredStocks.length);
  
  // Debug news bookmarks
  console.log('News bookmarks data:', newsBookmarks);
  console.log('Notes data:', notes);

  // Calculate metrics
  const completedTasks = tasks.filter(task => task.completed).length;
  const totalTasks = tasks.length;
  const completedBooks = books.filter(book => book.status === 'completed').length;
  const totalBooks = books.length;
  const currentlyReading = books.filter(book => book.status === 'reading').length;
  
  // Books completed this month
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  const booksThisMonth = books.filter(book => {
    if (book.status !== 'completed') return false;
    if (!book.completedAt && !book.completedDate) return false;
    
    const completedDate = new Date(book.completedAt || book.completedDate);
    return completedDate.getMonth() === currentMonth && 
           completedDate.getFullYear() === currentYear;
  }).length;
  
  const totalSleepRecords = sleepRecords.length;
  const totalNewsBookmarks = newsBookmarks.length;
  const totalNotes = notes.length;
  const totalGymSessions = gymSessions.length;
  const totalMeals = meals.length;

  // Sync function to refresh all data
  const handleSync = async () => {
    setIsSyncing(true);
    try {
      await Promise.all([
        refreshTasks(),
        refreshBooks(),
        refreshGym(),
        refreshMeals(),
        refreshSleep(),
        refreshNews(),
        refreshNotes(),
        refreshFirebaseStocks(),
        refreshStarredStocks()
      ]);
      console.log('All data synced successfully');
    } catch (error) {
      console.error('Error syncing data:', error);
    } finally {
      setIsSyncing(false);
    }
  };

  // Navigate to track page
  const handleDetails = () => {
    navigate('/track');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Metrics Section with Image */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 gap-y-12 md:grid-cols-2 md:items-center md:gap-x-12 lg:gap-x-20">
            <div>
              <h2 className="text-2xl font-bold mb-6 text-black">Your life at a glance</h2>
              <p className="text-slate-600 mb-8">Comprehensive tracking for every aspect of your personal journey.</p>
              
              {/* Metric Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                <div className="p-4 bg-white rounded-lg shadow-sm border border-gray-200">
                  <h3 className="font-semibold mb-2 text-black">Tasks completed this week</h3>
                  <p className="text-sm text-slate-600 mb-2">Monitor your productivity and progress.</p>
                  <div className="text-xl font-bold text-black">
                    {tasksLoading ? '...' : `${completedTasks}/${totalTasks}`}
                  </div>
                </div>
                
                <div className="p-4 bg-white rounded-lg shadow-sm border border-gray-200">
                  <h3 className="font-semibold mb-2 text-black">Stocks being watched</h3>
                  <p className="text-sm text-slate-600 mb-2">Track your investment portfolio.</p>
                  <div className="text-xl font-bold text-black">
                    {firebaseStocksLoading ? '...' : totalStocks}
                  </div>
                </div>

                <div className="p-4 bg-white rounded-lg shadow-sm border border-gray-200">
                  <h3 className="font-semibold mb-2 text-black">Books read this month</h3>
                  <p className="text-sm text-slate-600 mb-2">Track your reading progress.</p>
                  <div className="text-xl font-bold text-black">
                    {booksLoading ? '...' : booksThisMonth}
                  </div>
                </div>

                <div className="p-4 bg-white rounded-lg shadow-sm border border-gray-200">
                  <h3 className="font-semibold mb-2 text-black">Sleep records</h3>
                  <p className="text-sm text-slate-600 mb-2">Track your sleep patterns.</p>
                  <div className="text-xl font-bold text-black">
                    {sleepLoading ? '...' : totalSleepRecords}
                  </div>
                </div>

                <div className="p-4 bg-white rounded-lg shadow-sm border border-gray-200">
                  <h3 className="font-semibold mb-2 text-black">News bookmarks</h3>
                  <p className="text-sm text-slate-600 mb-2">Saved articles for later.</p>
                  <div className="text-xl font-bold text-black">
                    {newsLoading ? '...' : totalNewsBookmarks}
                  </div>
                </div>

                <div className="p-4 bg-white rounded-lg shadow-sm border border-gray-200">
                  <h3 className="font-semibold mb-2 text-black">Personal notes</h3>
                  <p className="text-sm text-slate-600 mb-2">Your thoughts and ideas.</p>
                  <div className="text-xl font-bold text-black">
                    {notesLoading ? '...' : totalNotes}
                  </div>
                </div>

                <div className="p-4 bg-white rounded-lg shadow-sm border border-gray-200">
                  <h3 className="font-semibold mb-2 text-black">Personal growth</h3>
                  <p className="text-sm text-slate-600 mb-2">Fitness and nutrition tracking.</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="text-center">
                      <div className="text-lg font-bold text-black">{gymLoading ? '...' : totalGymSessions}</div>
                      <div className="text-xs text-slate-600">Gym</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-black">{mealsLoading ? '...' : totalMeals}</div>
                      <div className="text-xs text-slate-600">Meals</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <button 
                  onClick={handleSync}
                  disabled={isSyncing}
                  className="inline-flex items-center justify-center gap-3 rounded-lg whitespace-nowrap transition-all duration-200 ease-in-out focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 border border-gray-200 bg-white text-gray-900 px-6 py-3 hover:bg-gray-50"
                >
                  {isSyncing ? (
                    <>
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Syncing...
                    </>
                  ) : (
                    <>
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                      </svg>
                      Sync
                    </>
                  )}
                </button>
                <button 
                  onClick={handleDetails}
                  className="inline-flex items-center justify-center gap-3 rounded-lg whitespace-nowrap transition-all duration-200 ease-in-out focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 gap-2 text-gray-900 p-0 hover:text-gray-700"
                >
                  Details
                  <svg
                    stroke="currentColor"
                    fill="none"
                    strokeWidth="0"
                    viewBox="0 0 15 15"
                    height="1em"
                    width="1em"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M6.1584 3.13508C6.35985 2.94621 6.67627 2.95642 6.86514 3.15788L10.6151 7.15788C10.7954 7.3502 10.7954 7.64949 10.6151 7.84182L6.86514 11.8418C6.67627 12.0433 6.35985 12.0535 6.1584 11.8646C5.95694 11.6757 5.94673 11.3593 6.1356 11.1579L9.565 7.49985L6.1356 3.84182C5.94673 3.64036 5.95694 3.32394 6.1584 3.13508Z"
                      fill="currentColor"
                    ></path>
                  </svg>
                </button>
              </div>
            </div>
            <img
              src="window.jpg"
              className="w-full rounded-lg object-cover"
              alt="life overview"
            />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Dashboard;